import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { BookingServiceResponseDto, MeetingType } from '../../types/booking';
import { hasValidationErrors, ServiceFormValues, validateServiceForm } from '../../utils/bookingValidators';

interface Props {
  isOpen: boolean;
  service: BookingServiceResponseDto | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (values: ServiceFormValues) => void;
}

const EMPTY_FORM: ServiceFormValues = {
  name: '',
  description: '',
  durationMinutes: '',
  price: '',

  meetingType: MeetingType.Online,
  meetingLocation: '',

  active: true,
  sortOrder: 0,
};

export default function ServiceModal({ isOpen, service, saving, onClose, onSave }: Props) {
  const [values, setValues] = useState<ServiceFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(
        service
          ? {
              name: service.name,
              description: service.description ?? '',
              durationMinutes: service.durationMinutes,
              price: service.price ?? '',

              meetingType:
                service.meetingType ?? MeetingType.Online,

              meetingLocation:
                service.meetingLocation ?? '',

              active: service.active,
              sortOrder: service.sortOrder,
            }
          : EMPTY_FORM
      );
      setErrors({});
      setIsDirty(false);
    }
  }, [isOpen, service]);

  if (!isOpen) return null;

  const handleChange = (field: keyof ServiceFormValues, value: string | number | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateServiceForm(values);
    setErrors(validationErrors);
    if (hasValidationErrors(validationErrors)) return;
    onSave(values);
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-strivre-navy/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-4">
          <h2 className="text-lg font-bold text-strivre-navy">{service ? 'Edit Service' : 'Create Service'}</h2>
          <button onClick={handleClose} className="rounded-lg p-2 hover:bg-strivre-softgrey" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Service Name</label>
            <input className="input-base" value={values.name} onChange={(e) => handleChange('name', e.target.value)} />
            {errors.name && <p className="mt-1 text-xs text-strivre-burgundy">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">Description</label>
            <textarea className="input-base" rows={3} value={values.description} onChange={(e) => handleChange('description', e.target.value)} />
            {errors.description && <p className="mt-1 text-xs text-strivre-burgundy">{errors.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Duration (Minutes)</label>
              <input type="number" min={1} className="input-base" value={values.durationMinutes} onChange={(e) => handleChange('durationMinutes', e.target.value)} />
              {errors.durationMinutes && <p className="mt-1 text-xs text-strivre-burgundy">{errors.durationMinutes}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">
                Price
                <span className="ml-1 font-normal text-slate-400">
                  (Optional)
                </span>
              </label>

              <input
                type="number"
                min={0}
                step="0.01"
                className="input-base"
                value={values.price ?? ""}
                onChange={(e) =>
                  handleChange("price", e.target.value)
                }
                placeholder="Leave blank if no price"
              />
              {errors.price && <p className="mt-1 text-xs text-strivre-burgundy">{errors.price}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">
                Meeting Type
              </label>

              <select
                className="input-base"
                value={values.meetingType}
                onChange={(e) => {
                  const meetingType = Number(
                    e.target.value
                  ) as MeetingType;

                  handleChange(
                    'meetingType',
                    meetingType
                  );

                  if (meetingType !== MeetingType.OnSite) {
                    handleChange('meetingLocation', '');
                  }
                }}
              >
                <option value={MeetingType.Online}>
                  Online
                </option>

                <option value={MeetingType.OnSite}>
                  On-Site
                </option>

                <option value={MeetingType.Phone}>
                  Phone
                </option>
              </select>
            </div>
            {values.meetingType === MeetingType.OnSite && (
              <div>
                <label className="block text-sm font-medium text-strivre-darktext mb-1">
                  Meeting Location / Address
                </label>

                <input
                  className="input-base"
                  value={values.meetingLocation}
                  onChange={(e) =>
                    handleChange(
                      'meetingLocation',
                      e.target.value
                    )
                  }
                  placeholder="e.g. 123 Main Street, Singapore"
                />

                {errors.meetingLocation && (
                  <p className="mt-1 text-xs text-strivre-burgundy">
                    {errors.meetingLocation}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Status</label>
              <select className="input-base" value={values.active ? 'Active' : 'Inactive'} onChange={(e) => handleChange('active', e.target.value === 'Active')}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Sort Order</label>
              <input type="number" min={0} className="input-base" value={values.sortOrder} onChange={(e) => handleChange('sortOrder', e.target.value)} />
              {errors.sortOrder && <p className="mt-1 text-xs text-strivre-burgundy">{errors.sortOrder}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={handleClose} className="btn-outline btn-md">Cancel</button>
            <button type="submit" disabled={saving} className="btn-md text-white bg-strivre-navy hover:bg-strivre-navy/90 rounded-lg px-4 py-2 font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
